<template>
  <section>
    <b-row>
      <b-col cols="12">
        <b-card class="lead-details-card">
  <!-- Edit Button -->
  <b-row class="justify-content-end mb-2">
    <b-col cols="auto">
      <template v-if="$permissionAbility(LEAD_EDIT, permissions)">
        <b-button
          variant="outline-primary"
          class="btn-icon rounded-circle"
          @click="onShowEditLead(lead)"
        >
          <feather-icon icon="Edit2Icon" />
        </b-button>
      </template>
    </b-col>
  </b-row>

  <b-row>
    <b-col cols="12">
      <b-card>
        <!-- First Info Row -->
        <b-row class="gy-3">
          <b-col
            v-for="(field, index) in [
              { label: 'Title', value: lead?.title },
              { label: 'Key Contact Person', value: lead?.key_contact_person },
              { label: 'Company Name', value: lead?.company_name },
            ]"
            :key="'info1-' + index"
            cols="12"
            sm="6"
            lg="2"
            class="mb-1"
          >
            <h6 class="text-capitalize">{{ field.label }}</h6>
            <b-card-text v-if="field.value">{{ field.value }}</b-card-text>
            <b-card-text v-else>N/A</b-card-text>
          </b-col>

          <!-- Email -->
          <b-col cols="12" sm="6" lg="2" class="mb-1">
            <h6 class="text-capitalize">Email</h6>
            <template v-if="lead?.email">
              <b-link :href="'mailto:' + lead?.email">{{ lead?.email }}</b-link>
            </template>
            <template v-else>
              <b-card-text>N/A</b-card-text>
            </template>
          </b-col>

          <!-- Website -->
          <b-col cols="12" sm="6" lg="2" class="mb-1">
            <h6 class="text-capitalize">Website Link</h6>
            <template v-if="lead?.website_link">
              <b-link :href="lead?.website_link" target="_blank">{{ lead?.website_link }}</b-link>
            </template>
            <template v-else>
              <b-card-text>N/A</b-card-text>
            </template>
          </b-col>

          <!-- Status -->
          <b-col cols="12" sm="6" lg="2" class="mb-1" >
            <h6 class="text-capitalize">Status</h6>
            <template v-if="lead?.lead_status_id">
              <b-badge
                pill
                :style="{ background: lead?.leadStatus?.data?.color_code }"
                class="d-inline-block"
              >
                {{ lead?.leadStatus?.data?.name }}
              </b-badge>

              <template
                v-if="lead?.leadStatus?.data?.type == 2 && !lead?.estimation_sent"
              >
                <b-badge class="badge-glow" variant="danger">Estimation Not Sent</b-badge>
              </template>

              <template
                v-else-if="lead?.leadStatus?.data?.type == 2 && lead?.estimation_sent"
              >
                <b-badge
                  class="badge-glow"
                  variant="success"
                  @click="viewOnlyEstimationModal"
                  style="cursor:pointer"
                >
                  Estimation Sent
                </b-badge>
              </template>
            </template>
          </b-col>
        </b-row>

        <!-- Second Info Row -->
        <b-row class="gy-3">
          <b-col cols="12" sm="6" lg="2" class="mb-1">
            <h6 class="text-capitalize">Source</h6>
            <b-card-text v-if="lead?.lead_source_id">{{ lead?.leadSource?.data?.name }}</b-card-text>
          </b-col>

          <b-col cols="12" sm="6" lg="2" class="mb-1">
            <h6 class="text-capitalize">Category</h6>
            <b-card-text v-if="lead?.lead_category_id">{{ lead?.leadCategory?.data?.name }}</b-card-text>
          </b-col>

          <b-col cols="12" sm="6" lg="2" class="mb-1">
            <h6 class="text-capitalize">Mobile</h6>
            <template v-if="lead?.mobile">
              <b-link :href="'tel:' + lead?.mobile">{{ lead?.mobile }}</b-link>
            </template>
          </b-col>

          <b-col cols="12" sm="6" lg="2" class="mb-1">
            <h6 class="text-capitalize">Tel</h6>
            <template v-if="lead?.phone">
              <b-link :href="'tel:' + lead?.phone">{{ lead?.phone }}</b-link>
            </template>
            <template v-else><b-card-text>N/A</b-card-text></template>
          </b-col>

          <b-col cols="12" sm="6" lg="2" class="mb-1">
            <h6 class="text-capitalize">Last Contact</h6>
            <b-card-text v-if="lead?.created_at">
              {{ lead?.created_at | moment("ddd, MMM Do YYYY, h:mm a") }}
            </b-card-text>
          </b-col>

          <b-col cols="12" sm="6" lg="2" class="mb-1">
            <h6 class="text-capitalize">Next Contact</h6>
            <b-card-text v-if="lead?.next_contact_date">
              {{ lead?.next_contact_date | moment("ddd, MMM Do YYYY, h:mm a") }}
            </b-card-text>
            <b-card-text v-else>N/A</b-card-text>
          </b-col>
        </b-row>

        <!-- Third Info Row -->
        <b-row class="gy-3 mt-2">
          <b-col cols="12" sm="6" lg="2" class="mb-1">
            <h6 class="text-capitalize">Department</h6>
            <b-card-text v-if="lead?.department_id">{{ lead?.department?.data?.name }}</b-card-text>
          </b-col>

          <b-col cols="12" sm="6" lg="4" class="mb-1">
            <h6 class="text-capitalize">Key Contact Manager</h6>
            <b-card-text v-if="lead?.team">
              <UserCard :user="lead?.accountManager?.data" />
            </b-card-text>
          </b-col>

          <b-col cols="12" sm="6" lg="4" class="mb-1">
            <h6 class="text-capitalize">Assigned To</h6>
            <b-card-text v-if="lead?.team">
              <span v-for="(item, index) in lead.team.data" :key="index">
                <UserCard :user="item" />
              </span>
            </b-card-text>
          </b-col>

          <b-col cols="12" sm="6" lg="2" class="mb-1">
            <h6 class="text-capitalize">Address</h6>
            <b-card-text v-if="lead?.address">
              {{ lead?.address }}<br>
              State: {{ lead?.state || "N/A" }}, City: {{ lead?.city || "N/A" }},<br>
              Street: {{ lead?.street || "N/A" }}, Zip: {{ lead?.zip_code || "N/A" }}
            </b-card-text>
          </b-col>

        
          
        </b-row>

        <!-- Business Cards -->
        <b-row class="gy-3 mt-2">

          <b-col cols="12" sm="6" lg="2" class="mb-1">
            <h6 class="text-capitalize">Country</h6>
            <b-card-text v-if="lead?.country_id">{{ lead?.leadCountry?.data?.name }}</b-card-text>
          </b-col>
          <b-col cols="12" sm="6" lg="2" class="mb-1">
            <h6 class="text-capitalize">Note</h6>
            <b-card-text>{{ lead?.note || "N/A" }}</b-card-text>
          </b-col>

          <b-col cols="12" sm="6" lg="2" class="mb-1">
            <h6 class="text-capitalize">Business Card (Front)</h6>
            <template v-if="lead?.business_card_front">
              <b-img :src="lead?.business_card_front" class="img-fluid rounded shadow-sm" />
            </template>
            <template v-else>
              <b-badge pill variant="danger">N/A</b-badge>
            </template>
          </b-col>

          <b-col cols="12" sm="6" lg="2" class="mb-1">
            <h6 class="text-capitalize">Business Card (Back)</h6>
            <template v-if="lead?.business_card_back">
              <b-img :src="lead?.business_card_back" class="img-fluid rounded shadow-sm" />
            </template>
            <template v-else>
              <b-badge pill variant="danger">N/A</b-badge>
            </template>
          </b-col>
        </b-row>
      </b-card>
    </b-col>
  </b-row>
</b-card>

      </b-col>
    </b-row>

    <b-row>
      <b-col
        lg="12"
        cols="12"
      >
        <b-card>
          <div>
            <!-- search input -->
            <template v-if="lead?.leadStatus?.data?.type == 2">
              <b-alert
                variant="success"
                show
              >
                <h4 class="alert-heading">
                  This lead has been converted successfully & locked!
                  <template v-if="lead?.estimation_sent == false">
                    <b-button
                      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                      class="flex-shrink-0 ml-1"
                      variant="success"
                      @click="showEstimationModal"
                    >
                      Financial Estimation
                    </b-button>
                  </template>


                  <template v-if="lead?.estimation_sent == true && lead?.project?.data?.status == false">
                    <b-button
                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                        class="flex-shrink-0 ml-1"
                        variant="success"
                        @click="showEstimationModal"
                    >
                      Edit Financial Estimation
                    </b-button>
                  </template>
                </h4>
              </b-alert>
            </template>
            <template v-else-if="lead?.leadStatus?.data?.type == 3">
              <b-alert
                variant="danger"
                show
              >
                <h4 class="alert-heading">
                  This lead has been lost & locked!
                </h4>
              </b-alert></template>
            <div
              class="custom-search d-flex align-items-center justify-content-end"
            >
              <div
                class="d-flex flex-column flex-sm-row align-items-center mb-1 justify-content-around"
              >
                <template
                  v-if="$permissionAbility(LEAD_ACTIVITY_CREATE, permissions)"
                >
                  <template v-if="lead?.leadStatus?.data?.type == 1">
                    <b-button
                      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                      class="flex-shrink-0"
                      variant="primary"
                      @click="newLogModal"
                    >
                      Create Activity Log
                    </b-button>
                  </template>
                </template>
              </div>
            </div>

            <vue-good-table
              mode="remote"
              style-class="vgt-table lead-details-table striped condensed"
              :total-rows="totalRecords"
              :is-loading.sync="isLoading"
              :rows="rows"
              :sort-options="{
                enabled: true,
                multipleColumns: true,
                initialSortBy: [{ field: 'last_contact_date', type: 'desc' }],
              }"
              :columns="columns"
              :pagination-options="{
                enabled: true,
                perPage: pageLength,
              }"
              @on-page-change="onPageChange"
              @on-sort-change="onSortChange"
              @on-column-filter="onColumnFilter"
              @on-per-page-change="onPerPageChange"
            >
              <template
                slot="table-row"
                slot-scope="props"
              >
                <!-- File -->

                <template v-if="props?.column?.field === 'format_file'">

                  <b-button
                    v-if="props.row?.file.length > 0"
                    v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                    variant="outline-primary"
                    class="btn-sm"
                    @click="showAttachmentModal(props.row?.file)"
                  >
                    <feather-icon icon="DownloadIcon" />
                  </b-button>

                  <template v-else>
                    <b>No Files To Download</b>
                  </template>
                </template>

                <template v-if="props?.column?.field === 'format_remarks'">
                  <b-button
                    v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                    variant="outline-primary"
                    class="btn-sm"
                    @click="showDescriptionModal(props.row?.remarks)"
                  >
                    <feather-icon icon="InfoIcon" />
                  </b-button>
                </template>

                <template v-if="props?.column?.field === 'format_lead_status'">
                  <b-badge
                    pill
                    :style="{
                      background: props?.row?.leadStatus?.data?.color_code,
                    }"
                  >
                    {{ props?.row?.leadStatus?.data?.name }}
                  </b-badge>
                </template>
                <template v-if="props?.column?.field === 'format_created_by'">
                  {{ props?.row?.created_by?.name }}
                </template>
                <span v-if="props?.column?.field === 'action'">

                  <template v-if="$permissionAbility(LEAD_ACTIVITY_EDIT, permissions)">
                    <span @click="onShow(props.row)">
                      <feather-icon
                        v-b-tooltip.hover
                        icon="Edit2Icon"
                        class="mr-50 custom-icon cursor-pointer"
                        title="Edit"
                        variant="warning"
                        size="16"
                      />
                    </span>
                  </template>

                  <template v-if="$permissionAbility(LEAD_ACTIVITY_DELETE, permissions)">
                    <span @click="onDelete(props?.row?.id)">

                      <feather-icon
                        v-b-tooltip.hover
                        icon="TrashIcon"
                        class="mr-50 custom-icon cursor-pointer"
                        variant="danger"
                        title="Delete"
                        size="16"
                      />
                    </span>
                  </template>
                </span>

                <!-- Column: Common -->
                <span v-else>
                  {{ props.formattedRow[props?.column?.field] }}
                </span>
              </template>

              <!-- pagination -->
              <template
                slot="pagination-bottom"
                slot-scope="props"
              >
                <div class="d-flex justify-content-between flex-wrap">
                  <div class="d-flex align-items-center mb-0 mt-1">
                    <span class="text-nowrap"> Showing 1 to </span>
                    <b-form-select
                      v-model="pageLength"
                      :options="['10', '25', '50', '100', '500']"
                      class="mx-1"
                      @input="
                        (value) =>
                          props.perPageChanged({ currentPerPage: value })
                      "
                    />
                    <span class="text-nowrap">
                      of {{ props.total }} entries
                    </span>
                  </div>
                  <div>
                    <b-pagination
                      :value="1"
                      :total-rows="props?.total"
                      :per-page="pageLength"
                      first-number
                      last-number
                      align="right"
                      prev-class="prev-item"
                      next-class="next-item"
                      class="mt-1 mb-0"
                      @input="
                        (value) => props.pageChanged({ currentPage: value })
                      "
                    >
                      <template #prev-text>
                        <feather-icon
                          icon="ChevronLeftIcon"
                          size="18"
                        />
                      </template>
                      <template #next-text>
                        <feather-icon
                          icon="ChevronRightIcon"
                          size="18"
                        />
                      </template>
                    </b-pagination>
                  </div>
                </div>
              </template>
            </vue-good-table>
          </div>
          <b-modal
            id="description-modal"
            centered
            title="Remarks"
            hide-footer
            no-close-on-backdrop
            @hidden="hiddenDescriptionModal"
          >
            <b-card-text>
              {{ modalDescription }}
            </b-card-text>
          </b-modal>

          <b-modal
            id="attachment-modal"
            centered
            title="Attachments"
            hide-footer
            no-close-on-backdrop
            @hidden="hiddenAttachmentModal"
          >
            <b-card-text v-if="selectedAttachments && Array.isArray(selectedAttachments)">
              <b-row>
                <b-col
                  v-for="(fileUrl, index) in selectedAttachments"
                  :key="index"
                  class="mb-1"
                  cols="6"
                >
                  <div class="d-flex align-items-center justify-content-between">
                    <h5 class="mb-0">
                      <b-link
                        :href="fileUrl.original_url"
                        target="_blank"
                        class="d-flex align-items-center"
                      >
                        <feather-icon
                          icon="DownloadIcon"
                          class="mr-2"
                        />
                        Attachment
                      </b-link>
                    </h5>

                    <b-button
                      variant="danger"
                      size="sm"
                      class="ml-2"
                      @click="deleteAttachment(fileUrl)"
                    >
                      X
                    </b-button>
                  </div>
                </b-col>
              </b-row>

            </b-card-text>

          </b-modal>
          <!-- Activity Log Modal -->
          <b-modal
            id="modal-activity-log-show-form"
            centered
            :title="
              modelType == 'editModel'
                ? 'Edit Activity Log'
                : 'Create Activity Log '
            "
            hide-footer
            no-close-on-backdrop
            @hidden="hiddenModal"
          >
            <validation-observer ref="leadActvityLogValidation">
              <b-form @submit.prevent="validationForm">
                <b-row>
                  <template
                    v-if="$permissionAbility(LEAD_ACTIVITY_EDIT, permissions)"
                  >
                    <b-col
                      md="10"
                      lg="10"
                      xs="12"
                    >
                      <b-form-group
                        label="Lead Status "
                        label-for="leadStatusId"
                        class="required-label"
                      >
                        <ValidationProvider
                          v-slot="{ errors }"
                          name="lead status"
                          vid="lead_status_id"
                          rules="required"
                        >
                          <v-select
                            id="division-id"
                            v-model="leadStatusId"
                            placeholder="Assign a Status"
                            :options="statusIdOption"
                            :reduce="(country) => country.id"
                            label="name"
                            @input="checkForFinalStatus"
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col>
                    <b-col
                      md="2"
                      lg="2"
                      xs="12"
                    >
                      <b-button
                        v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                        v-b-tooltip.hover.top="'Reset Status List!'"
                        variant="outline-secondary"
                        class="btn-icon rounded-circle mt-2"
                        @click="loadAllStatus"
                      >
                        <feather-icon icon="RefreshCcwIcon" />
                      </b-button>
                    </b-col>
                  </template>
                  <template
                    v-if="!$permissionAbility(LEAD_ACTIVITY_EDIT, permissions)"
                  >
                    <b-col
                      md="12"
                      lg="12"
                      xs="12"
                    >
                      <b-form-group
                        label="Lead Status *"
                        label-for="leadStatusId"
                      >
                        <ValidationProvider
                          v-slot="{ errors }"
                          name="lead status"
                          vid="lead_status_id"
                          rules="required"
                        >
                          <v-select
                            id="division-id"
                            v-model="leadStatusId"
                            placeholder="Assign a Status"
                            :options="statusIdOption"
                            :reduce="(item) => item.id"
                            label="name"
                            @input="checkForFinalStatus"
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col></template>
                </b-row>

                <b-form-group
                    label="Contact Date & Time"
                    label-for="contact_date"
                    class="required-label"
                >
                  <ValidationProvider
                      v-slot="{ errors }"
                      name="contact date"
                      vid="contact_date"
                      :rules="'required'"
                  >
                    <flat-pickr
                        id="contact-date"
                        v-model="contactDate"
                        class="form-control"
                        :state="errors.length > 0 ? false : null"
                        :config="contactDateConfig"
                        placeholder="Select Date & Time"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </ValidationProvider>
                </b-form-group>

                <!-- select contact medium -->
                <b-form-group
                  label="Contact Medium "
                  label-for="contact-through"
                  class="required-label"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    name="contact medium"
                    vid="contact_through"
                    rules="required"
                  >
                    <v-select
                      id="contact-through"
                      v-model="contactMedium"
                      :options="selectContactMediumOption"
                      :reduce="(medium) => medium.value"
                      placeholder="Choose A Contact Medium"
                      label="text"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </ValidationProvider>
                </b-form-group>

                <!-- remarks -->
                <b-form-group
                  label="Remarks "
                  label-for="remarks"
                  class="required-label"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    name="remarks"
                    vid="remarks"
                    rules="required"
                  >
                    <b-form-textarea
                      id="remarks"
                      v-model="remarks"
                      type="text"
                      :state="errors.length > 0 ? false : null"
                      placeholder="Enter Meeting Remarks"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </ValidationProvider>
                </b-form-group>

                <!-- file -->
                <b-form-group
                  :label="selectedLeadStatus?.type === 2 ? 'Project Proposal' : 'Attach File'"
                  label-for="file"
                  :class="{'required-label': selectedLeadStatus?.type === 2}"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="attachment"
                    :rules="fileValidationRules"
                    vid="lead_activity_log_file"
                  >
                    <b-form-file
                      :id="`file-${selectedLeadStatus?.type}`"
                      v-model="file"
                      multiple
                      :state="errors.length ? false : null"
                      placeholder="Choose a file or drop it here..."
                      drop-placeholder="Drop file here..."
                      @change="handleFileChange"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
                <b-form-group
                  label="Next Contact Date & Time"
                  label-for="next_contact_date"
                  :class="{ 'required-label': selectedLeadStatus?.type !== 2 && selectedLeadStatus?.type !== 3 }"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    name="next contact date"
                    vid="next_contact_date"
                    :rules="selectedLeadStatus?.type !== 2 && selectedLeadStatus?.type !== 3 ? 'required' : ''"
                  >
                    <flat-pickr
                      id="next-contact-date"
                      v-model="nextContactDate"
                      class="form-control"
                      :state="errors.length > 0 ? false : null"
                      :config="contactDateConfig"
                      placeholder="Select Date & Time"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </ValidationProvider>
                </b-form-group>

                <!-- loading button -->
                <template v-if="isLeadActivityFormSubmitLoading">
                  <b-button
                    class="float-right"
                    variant="primary"
                    disabled
                  >
                    <b-spinner small />
                    Loading...
                  </b-button>
                </template>

                <!-- submit button -->
                <template v-else>
                  <b-button
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    type="submit"
                    class="float-right"
                    variant="primary"
                  >
                    Submit
                  </b-button>
                </template>
              </b-form>
            </validation-observer>
          </b-modal>

          <!-- Financial Estimation Modal -->

          <FinancialEstimationForm
            :selected-lead-info="selectedLeadInfo"
            :lead-title="leadTitle"
            :lead-data="lead"
            :view-only="viewOnly"
            @closed="hideEstimationModal"
            @loadLeadInfo="updateLeadInfo($event)"
          />
        </b-card>
      </b-col>
    </b-row>
    <b-modal
      id="modal-lead-form"
      centered
      :title="
        modelType == 'editModel'
          ? 'Edit Lead  Details '
          : modelType == 'copyModel'
          ? 'Copy Lead '
          : 'Add Lead '
      "
      hide-footer
      @hidden="hiddenLeadEditModal"
      size="lg"
      no-close-on-backdrop
    >
      <validation-observer ref="leadValidation">
        <b-form v-on:submit.prevent="validationLeadForm">
          <b-row>
            <b-col md="12" lg="12" xs="12">
              <!-- title  -->
              <b-form-group
                label="Lead Title "
                label-for="title"
                class="required-label"
              >
                <validation-provider
                  #default="{ errors }"
                  name="title"
                  vid="title"
                  rules="required|max:255"
                >
                  <b-form-input
                    id="name"
                    type="text"
                    v-model="title"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Enter Lead Title"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row class="mt-2 mb-1">
            <b-col md="12" lg="12" xs="12"> <h5>Lead Information</h5></b-col>
          </b-row>
          <b-row>
            <b-col md="4" lg="4" xs="12">
              <b-form-group
                label="Company Name "
                label-for="company_name"
                class="required-label"
              >
                <validation-provider
                  #default="{ errors }"
                  name="company name"
                  vid="company_name"
                  rules="required|max:255"
                >
                  <b-form-input
                    id="name"
                    type="text"
                    v-model="company_name"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Enter Company Name"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="4" lg="4" xs="12">
              <b-form-group label="Email " label-for="email">
                <validation-provider
                  #default="{ errors }"
                  name="company email"
                  vid="email"
                >
                  <b-form-input
                    id="name"
                    type="email"
                    v-model="email"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Enter Contact Email"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="4" lg="4" xs="12">
              <b-form-group
                label="Country "
                label-for="country_id"
                class="required-label"
              >
                <ValidationProvider
                  name="country name"
                  v-slot="{ errors }"
                  vid="country_id"
                  rules="required"
                >
                  <v-select
                    id="division-id"
                    placeholder="Choose here"
                    v-model="country_id"
                    :options="countryIdOption"
                    :reduce="(item) => item?.id"
                    v-on:input="setDialCodeAsCountry"
                    label="name"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="12" lg="12" xs="12" class="mb-10">
              <b-form-group label="Billing Address" label-for="billing_address">
                <ValidationProvider
                    #default="{ errors }"
                    name="billing address"
                    vid="billing_address"
                >
                  <div :class="{ fullscreen: isFullscreen }">
                    <quill-editor
                        v-model="billing_address"
                        :options="editorOption"
                        :style="{
                        width: '98% !important'
                      }"
                        id="billing_address"
                        :state="errors.length > 0 ? false : null"
                        placeholder="Billing Address"
                        class="custom-quill-editor"
                    >
                      <div id="toolbar" slot="toolbar">
                        <!-- Add a bold button -->
                        <span class="ql-formats">
                        <button class="ql-bold">
                          Bold
                        </button>
                        <button class="ql-italic">
                          Italic
                        </button>
                        <button class="ql-underline">
                          Underline
                        </button>
                        <button class="ql-strike">
                          Strike
                        </button>
                        <button
                            class="ql-blockquote"
                        ></button>
                        <button
                            class="ql-list"
                            value="ordered"
                        ></button>
                        <button
                            class="ql-list"
                            value="bullet"
                        ></button>
                        <button
                            class="ql-script"
                            value="sub"
                        ></button>
                        <button
                            class="ql-script"
                            value="super"
                        ></button>
                        <button
                            class="ql-indent"
                            value="-1"
                        ></button>
                        <button
                            class="ql-indent"
                            value="+1"
                        ></button>
                        <button
                            class="ql-direction"
                            value="rtl"
                        ></button>
                        <button
                            class="ql-align"
                            value=""
                        ></button>
                        <button
                            class="ql-align"
                            value="center"
                        ></button>
                        <button
                            class="ql-align"
                            value="right"
                        ></button>
                        <button
                            class="ql-align"
                            value="justify"
                        ></button>
                        <select class="ql-color"></select>
                        <select
                            class="ql-background"
                        ></select>
                        <select class="ql-size">
                          <option value="small" />

                          <option selected />
                          <option value="large" />
                          <option value="huge" />
                        </select>

                        <select class="ql-font"></select>
                        <select class="ql-header">
                          <option value="1">
                            Heading 1
                          </option>
                          <option value="2">
                            Heading 2
                          </option>
                          <option value="3">
                            Heading 3
                          </option>
                          <option value="4">
                            Heading 4
                          </option>
                          <option value="5">
                            Heading 5
                          </option>
                          <option value="6">
                            Heading 6
                          </option>
                          <option selected>Normal</option>
                        </select>

                        <button class="ql-link"></button>
                        <button type="button" :title="isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'" class="fullscreen-btn" @click="toggleFullscreen">
                          🔳
                        </button>
                      </span>
                      </div>
                    </quill-editor>
                  </div>
                  <small class="text-danger">{{
                      errors[0]
                    }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>


            <b-col md="6" lg="7" xs="12">
              <b-form-group label="Company Address" label-for="address">
                <validation-provider
                  #default="{ errors }"
                  name="company address"
                  vid="address"
                  rules="max:255"
                >
                  <b-form-textarea
                    id="address"
                    v-model="address"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Enter Company Address"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="6" lg="5" xs="12">
              <b-form-group
                label="Company Website Link "
                label-for="website_link"
              >
                <validation-provider
                  #default="{ errors }"
                  name="website link"
                  vid="website_link"
                >
                  <b-form-input
                    id="website_link"
                    type="text"
                    v-model="website_link"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Enter Website Link"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="3" lg="3" xs="12">
              <b-form-group label="City" label-for="city">
                <validation-provider
                  #default="{ errors }"
                  name="city"
                  vid="city"
                  rules="max:255"
                >
                  <b-form-input
                    id="name"
                    type="text"
                    v-model="city"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Enter City Name"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="3" lg="3" xs="12">
              <b-form-group label="State " label-for="state">
                <validation-provider
                  #default="{ errors }"
                  name="state"
                  vid="state"
                >
                  <b-form-input
                    id="state"
                    type="text"
                    v-model="state"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Enter State Name"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="3" lg="3" xs="12">
              <b-form-group label="Street " label-for="street">
                <validation-provider
                  #default="{ errors }"
                  name="street"
                  vid="street"
                >
                  <b-form-input
                    id="street"
                    type="text"
                    v-model="street"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Enter Street Name"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="3" lg="3" xs="12">
              <b-form-group label="Zip Code " label-for="zip_code">
                <validation-provider
                  #default="{ errors }"
                  name="zip code"
                  vid="zip_code"
                >
                  <b-form-input
                    id="zip_code"
                    type="text"
                    v-model="zip_code"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Enter Zip Code"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row class="mt-2 mb-1">
            <b-col md="12" lg="12" xs="12">
              <h5>Key Contact Person Information</h5></b-col
            >
          </b-row>
          <b-row>
            <b-col md="3" lg="3" xs="12">
              <b-form-group
                label="Person Name "
                label-for="key_contact_person"
                class="required-label"
              >
                <validation-provider
                  #default="{ errors }"
                  name="person name"
                  vid="key_contact_person"
                  rules="required|max:255"
                >
                  <b-form-input
                    id="key_contact_person"
                    type="text"
                    v-model="keyContactPerson"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Enter Person Name"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="3" lg="3" xs="12">
              <b-form-group
                label="Dial Code "
                label-for="dial_code_id"
                class="required-label"
              >
                <ValidationProvider
                  name="dial code"
                  v-slot="{ errors }"
                  vid="dial_code_country_id"
                  :rules="mobile ? 'required' : ''"
                >
                  <v-select
                    id="division-id"
                    placeholder="Choose here"
                    v-model="dial_code_country_id"
                    :options="dialCodeCountryIdOption"
                    :reduce="(item) => item?.id"
                    label="dial_code"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
            <b-col md="3" lg="3" xs="12">
              <!-- mobile -->
              <b-form-group label="Mobile" label-for="mobile">
                <validation-provider
                  #default="{ errors }"
                  name="mobile"
                  vid="mobile"
                >
                  <b-form-input
                    id="name"
                    type="number"
                    v-model="mobile"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Enter Number"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="3" lg="3" xs="12">
              <!-- phone  -->
              <b-form-group label="Telephone" label-for="phone">
                <validation-provider
                  #default="{ errors }"
                  name="tel"
                  vid="phone"
                >
                  <b-form-input
                    id="name"
                    type="number"
                    v-model="phone"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Enter Number"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row class="mt-2 mb-1">
            <b-col md="12" lg="12" xs="12">
              <h5>Lead Assignee Information</h5></b-col
            >
          </b-row>

          <template>
            <div>
              <b-row>

                <b-col v-if="modelType != 'editModel'" cols="12">
                  <b-form-group
                      label="Lead Status"
                      label-for="lead_status_id"
                      class="required-label"
                  >
                    <ValidationProvider
                        name="lead status"
                        v-slot="{ errors }"
                        vid="lead_status_id"
                        rules="required"
                    >
                      <v-select
                          id="division-id"
                          placeholder="Select Status"
                          v-model="lead_status_id"
                          :options="statusIdOption"
                          :reduce="(item) => item.id"
                          label="name"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </ValidationProvider>
                  </b-form-group>
                </b-col>

                <b-col md="12" lg="12" xs="12">
                  <b-form-group
                      label="Key Account Manager"
                      label-for="kam"
                      class="required-label"
                  >
                    <ValidationProvider
                        name="key account manager"
                        v-slot="{ errors }"
                        vid="kam"
                    >
                      <v-select
                          id="kam"
                          placeholder="Select Employee"
                          v-model="kam"
                          :options="userIdOption"
                          :reduce="(item) => item?.id"
                          label="name"
                      >
                        <template #option="data">
                          <div v-if="modelType == 'editModel'" class="d-flex align-items-center">
                            <b-avatar :src="data?.avatar === '' ? '/avatar.svg' : data?.avatar" class="mr-1" />
                            <div>
                              <div class="font-weight-bolder">
                                {{ data?.name }}
                                <b-badge pill variant="warning">{{ data?.employee_number }}</b-badge>
                              </div>
                              <div class="font-small-2">{{ data?.email }}</div>
                            </div>
                          </div>
                          <UserSelect v-else :user="data" />
                        </template>
                      </v-select>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </ValidationProvider>
                  </b-form-group>
                </b-col>

                <b-col md="6" lg="6" xs="12">
                  <b-form-group
                      label="Assigned Employee"
                      label-for="assign_users"
                      class="required-label"
                  >
                    <ValidationProvider
                        name="assigned employee"
                        v-slot="{ errors }"
                        vid="assign_users"
                        :rules="modelType == 'editModel' ? '' : 'required'"
                    >
                      <v-select
                          id="user-id"
                          placeholder="Select Employee"
                          v-model="assign_users"
                          :options="userIdOption"
                          :reduce="(item) => item?.id"
                          label="name"
                          multiple
                      >
                        <template #option="data">
                          <div v-if="modelType == 'editModel'" class="d-flex align-items-center">
                            <b-avatar :src="data?.avatar === '' ? '/avatar.svg' : data?.avatar" class="mr-1" />
                            <div>
                              <div class="font-weight-bolder">
                                {{ data?.name }}
                                <b-badge pill variant="warning">{{ data?.employee_number }}</b-badge>
                              </div>
                              <div class="font-small-2">{{ data?.email }}</div>
                            </div>
                          </div>
                          <UserSelect v-else :user="data" />
                        </template>
                      </v-select>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </ValidationProvider>
                  </b-form-group>
                </b-col>

                <b-col md="6" lg="6" xs="12">
                  <b-form-group
                      label="Lead Source"
                      label-for="lead_source_id"
                      class="required-label"
                  >
                    <ValidationProvider
                        name="lead source"
                        v-slot="{ errors }"
                        vid="lead_source_id"
                        rules="required"
                    >
                      <v-select
                          id="head-id"
                          placeholder="Select Source"
                          v-model="lead_source_id"
                          :options="sourceIdOption"
                          :reduce="(item) => item?.id"
                          label="name"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </ValidationProvider>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col md="6" lg="6" xs="12">
                  <b-form-group
                      label="Lead Category"
                      label-for="lead_category_id"
                      class="required-label"
                  >
                    <ValidationProvider
                        name="lead category"
                        v-slot="{ errors }"
                        vid="lead_category_id"
                        rules="required"
                    >
                      <v-select
                          id="head-id"
                          placeholder="Select Category"
                          v-model="lead_category_id"
                          :options="categoryIdOption"
                          :reduce="(item) => item?.id"
                          label="name"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </ValidationProvider>
                  </b-form-group>
                </b-col>

                <b-col md="6" lg="6" xs="12">
                  <b-form-group
                      label="Department"
                      label-for="department_id"
                      class="required-label"
                  >
                    <ValidationProvider
                        name="department name"
                        v-slot="{ errors }"
                        vid="department_id"
                        rules="required"
                    >
                      <v-select
                          id="division-id"
                          placeholder="Select Department"
                          v-model="department_id"
                          :options="departmentIdOption"
                          :reduce="(item) => item?.id"
                          label="name"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </ValidationProvider>
                  </b-form-group>
                </b-col>
              </b-row>
            </div>
          </template>

          <b-row>
            <b-col cols="12">
              <!-- Note  -->
              <b-form-group label="Note" label-for="note">
                <validation-provider
                  #default="{ errors }"
                  name="note"
                  vid="note"
                  rules="max:255"
                >
                  <b-form-textarea
                    id="note"
                    v-model="note"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Take A Quick Note"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>
          <!-- -->

          <b-row>
            <b-col cols="12">
              <b-row>

                <b-col cols="6">
                  <template v-if="modelType === 'editModel'">

                    <b-img
                        :src="business_card_front"
                        class="small-image"
                    ></b-img>

                  </template>
                </b-col>
                <b-col cols="6">
                  <template v-if="modelType === 'editModel'">
                    <b-img
                        :src="business_card_back"
                        class="small-image"
                    ></b-img>
                  </template>

                </b-col>

                <b-col md="6">
                  <b-form-group
                      label="Business Card (Front)"
                      label-for="business_card_front"
                  >
                    <validation-provider
                        #default="{ errors }"
                        name="Business Card (Front)"
                        rules="size:2048|ext:jpeg,png,jpg"
                        vid="business_card_front"
                    >
                      <b-form-file
                          id="business_card_front"
                          v-model="business_card_front_input"
                          :state="errors.length > 0 ? false : null"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                </b-col>

                <b-col md="6">

                  <b-form-group
                      label="Business Card (Back)"
                      label-for="business_card_back"
                  >
                    <validation-provider
                        #default="{ errors }"
                        name="Business Card (Back)"
                        rules="size:2048|ext:jpeg,png,jpg"
                        vid="business_card_back"
                    >
                      <b-form-file
                          id="business_card_back"
                          v-model="business_card_back_input"
                          :state="errors.length > 0 ? false : null"
                          placeholder="Choose a file or drop it here..."
                          drop-placeholder="Drop file here..."
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <!-- loading button -->
          <template v-if="isLeadsFormSubmitLoading">
            <b-button class="float-right" variant="primary" disabled>
              <b-spinner small />
              Loading...
            </b-button>
          </template>

          <!-- submit button -->
          <template v-else>
            <b-button
              type="submit"
              class="float-right"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
            >
              Submit
            </b-button>
          </template>
        </b-form>
      </validation-observer>
    </b-modal>
  </section>
</template>
  <!--  -->
<script>
import {
  BLink,
  BCard,
  BAvatar,
  BBadge,
  BPagination,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BDropdown,
  BDropdownItem,
  BFormFile,
  BButton,
  BForm,
  BModal,
  BCol,
  BRow,
  BCardText,
  BFormTextarea,
  BSpinner,
  BAlert,
  VBTooltip,
  BFormDatepicker, BImg,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import Ripple from 'vue-ripple-directive'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import FinancialEstimationForm from '@/views/admin/finance/project/FinancialEstimationForm.vue'
import {
  required, max, size, ext, max_value, min_value,
} from '@validations'
import flatPickr from 'vue-flatpickr-component'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { mapGetters } from 'vuex'
import { projectTypeConstants } from '@/helpers/constant/projectTypeConstant'
import { paymentTypeConstants } from '@/helpers/constant/paymentTypeConstant'
import { leadStatusTypeConstants } from '@/helpers/constant/leadStatusTypeConstant'
import {
  LEAD_ACTIVITY_ACCESS,
  LEAD_ACTIVITY_CREATE,
  LEAD_ACTIVITY_EDIT,
  LEAD_ACTIVITY_DELETE,
  LEAD_EDIT,
} from '@/helpers/permissionsConstant'
import { quillEditor } from "vue-quill-editor";
import UserCard from '@/layouts/components/UserCard.vue'
import { formatDateRange } from '@/helpers/helpers'

export default {
  name: 'LeadDetailsView',
  components: {
    BImg,
    FinancialEstimationForm,
    BLink,
    BForm,
    BButton,
    BCard,
    VueGoodTable,
    BAvatar,
    BBadge,
    UserCard,
    BPagination,
    BFormFile,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BDropdown,
    BDropdownItem,
    ValidationProvider,
    ValidationObserver,
    BModal,
    BCol,
    BRow,
    BCardText,
    BSpinner,
    BFormTextarea,
    flatPickr,
    BAlert,
    BFormDatepicker,
    quillEditor,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      LEAD_ACTIVITY_ACCESS,
      LEAD_ACTIVITY_CREATE,
      LEAD_ACTIVITY_EDIT,
      LEAD_ACTIVITY_DELETE,
      LEAD_EDIT,
      projectTypeConstants,
      paymentTypeConstants,
      leadStatusTypeConstants,
      viewOnly: false,
      selectedAttachments: null,
      modalDescription: '',
      projectType: '',
      paymentType: '',
      amount: '',
      timeDuration: '',
      milestones: '',
      selectedLeadInfo: {},
      editEstimation: false,
      milestoneAmmount: [],
      milestoneDeadLine: [],
      milestonePaymentType: [],

      contactDateConfig: {
        enableTime: true,
        dateFormat: 'Y-m-d H:i:S',
        static: true,
        minuteIncrement: 1,
      },

      leadId: this.$route.params.id,
      leadTitle: '',
      leadActivityLogId: '',
      modelType: '',
      contactMedium: '',
      selectContactMediumOption: [
        {
          value: 'Face To Face Meeting',
          text: 'Through a Face To Face Meeting',
        },
        { value: 'Virtual Meeting', text: 'Through a Virtual Meeting' },
        { value: 'Email', text: 'Through an Email' },
        { value: 'Call', text: 'Through a Phone Call' },
        { value: 'Text', text: 'Through a Text' },
        { value: 'Proposal Sent', text: 'Though Sending Proposal' },
      ],
      remarks: '',
      lastContactDate: '',
      nextContactDate: '',
      contactDate: '',
      statusIdOption: [],
      leadStatusId: '',
      selectedLeadStatus: '',
      file: [],

      pageLength: 10,
      columns: [
        {
          label: 'Contact Medium',
          field: 'contact_through',
          sortable: false,
        },
        {
          label: 'Remarks',
          field: 'format_remarks',
          sortable: false,
        },
        {
          label: 'File',
          field: 'format_file',
          sortable: false,
        },
        {
          label: 'Status',
          field: 'format_lead_status',
          sortable: false,
        },
        {
          label: 'Listing Contact Date',
          field: 'created_at',
          formatFn: this.formatDate,
          sortable: false,
        },
        {
          label: 'Last Contact Date & Time',
          field: 'last_contact_date',
          formatFn: this.formatDate,
          sortable: false,
        },
        {
          label: 'Next Contact Date & Time',
          field: 'next_contact_date',
          formatFn: this.formatDate,
          sortable: false,
        },
        {
          label: 'Created By',
          field: 'format_created_by',
          sortable: false,
        },
        {
          label: 'Action',
          field: 'action',
          sortable: false,
        },
      ],
      rows: [],
      searchTerm: '',
      delayTimer: null,
      isLoading: false,
      isMileStoneLoading: false,
      isLeadActivityFormSubmitLoading: false,
      isFinancialEstimationFormLoading: false,
      totalRecords: 0,
      serverParams: {
        columnFilters: {},
        sort: [
          { field: 'id', type: 'desc' },
          { field: 'name', type: 'desc' },
          { field: 'created_at', type: 'desc' },
        ],
        page: 1,
        perPage: 10,
      },
      lead: {},
      isLoading: false,
      isLeadsFormSubmitLoading: false,
      mobile: "",
      country_id: "",
      countryIdOption: [],
      dial_code_country_id: "",
      dialCodeCountryIdOption: [],
      isFullscreen: false,
      editorOption: {
        modules: {
          toolbar: "#toolbar",
        },
      },
      userIdOption: [],
      lead_source_id: "",
      sourceIdOption: [],
      lead_category_id: "",
      categoryIdOption: [],
      department_id: "",
      departmentIdOption: [],
      business_card_front_input: null,
      business_card_back_input: null,
      kam: null,
      assigneeOptions: [],
      leadStatusInfo: [],
    }
  },
  computed: {
    ...mapGetters({
      permissions: 'userModule/getPermissions',
    }),

    fileValidationRules() {
      return this.selectedLeadStatus?.type === 2
        ? 'required|size:8048|ext:jpg,jpeg,png,pdf,csv,xlsx,txt,docx'
        : 'size:8048|ext:jpg,jpeg,png,pdf,csv,xlsx,txt,docx'
    },
  },

  async created() {
    this.loadLeadData();

    const [
        categories,
        sources,
        departments,
        activeUsers,
        countries,
      ] = await Promise.all([
        this.getLeadCategories(),
        this.getLeadSources(),
        this.getDepartments(),
        this.getActiveUsers(),
        this.getCountries(),
      ]);

    this.userIdOption = (activeUsers?.data?.data || []).map((item) => {
        return {
          avatar: item?.avatar,
          name: item?.name,
          email: item?.email,
          mobile: item?.mobile,
          employee_number: item?.employee_number,
          id: item?.id,
          departmentId: item?.department_id,
          inactive: !item?.status,
        }
    });

    // sources
      this.sourceIdOption = (sources?.data?.data || []).map((item) => {
        let name = item.name;

        return {
          name,
          id: item.id,
        };
      });
    // categories
      this.categoryIdOption = (categories?.data?.data || []).map((item) => {
        let name = item.name;

        return {
          name,
          id: item.id,
        };
      });
    
      // department
      this.departmentIdOption = (departments?.data?.data || []).map((item) => {
        let name = item.name;
        return {
          name,
          id: item.id,
        };
      });

      //country
      this.countryIdOption = (countries?.data?.data || []).map((item) => {
        let name = item.name;

        return {
          name,
          id: item.id,
        };
      });

      //dial code
      this.dialCodeCountryIdOption = (countries?.data?.data || []).map(
        (item) => {
          let dial_code = `${item.dial_code} (${item.name})`
          return {
            dial_code,
            id: item.id,
          };
        }
      );

      this.assigneeOptions = (activeUsers?.data?.data || []).map((item) => {
        return {
          avatar: item?.avatar,
          name: item?.name,
          email: item?.email,
          mobile: item?.mobile,
          employee_number: item?.employee_number,
          id: item?.id,
          departmentId: item?.department_id,
          inactive: !item?.status,
        }
      });
  },
  methods: {

    handleFileChange(event) {
      this.file = Array.from(event.target.files)
    },

    async deleteAttachment(attachment) {
      await this.$api.delete(`/api/lead-activity-media/${attachment?.model_id}/${attachment?.id}`)

      this.selectedAttachments = this.selectedAttachments.filter(item => item.id !== attachment.id)
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Success',
          icon: 'BellIcon',
          variant: 'success',
          text: 'Attachment deleted successfully',
        },
      })

      this.$bvModal.hide('attachment-modal')
      this.loadItems()
    },
    checkForFinalStatus() {
      this.selectedLeadStatus = (this.statusIdOption || []).find(item => {
        if (item?.type == 2 && item?.id == this.leadStatusId) {
          return item
        } if (item?.type == 3 && item?.id == this.leadStatusId) {
          return item
        }
      })
    },
    showDescriptionModal(remarks) {
      if (remarks) {
        this.modalDescription = remarks
      } else {
        this.modalDescription = 'N/A'
      }

      this.$bvModal.show('description-modal')
    },

    showAttachmentModal(attachment) {
      this.selectedAttachments = attachment
      this.$bvModal.show('attachment-modal')
    },
    hiddenDescriptionModal() {
      this.$bvModal.hide('description-modal')
    },
    hiddenAttachmentModal() {
      this.selectedAttachments = null
      this.$bvModal.hide('attachment-modal')
    },
    async loadLeadData() {
      try {
        const { id } = this.$route.params
        const resLead = await this.$api.get(
          `api/leads/${id}?include=user,leadCategory,leadSource,leadStatus,department,leadCountry,dialCodeCountry,team,project,project.milestones, accountManager`,
        )

        const dataLead = resLead?.data?.data

        this.leadTitle = dataLead?.title

        this.lead = dataLead
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Warning',
            icon: 'BellIcon',
            variant: 'warning',
            text: error?.response?.data?.message,
          },
        })
      }

      this.loadAvailableStatus()
    },
    updateMilestonePaymentType() {
      this.milestonePaymentType = []
      for (let i = 0; i < this.milestones; i++) {
        this.milestonePaymentType.push(this.paymentType)
      }
    },

    loadMileStone() {
      this.milestoneAmmount = []
      this.milestonePaymentType = []
      for (let i = 0; i < this.milestones; i++) {
        this.milestoneAmmount.push(this.amount / this.milestones)
        this.milestonePaymentType.push(this.paymentType)
      }
      this.isMileStoneLoading = false
      return true
    },

    updateMilestoneInfo() {
      this.isMileStoneLoading = true
      setTimeout(this.loadMileStone, 300)
    },
    async loadAllStatus() {
      // status
      const status = await this.$api.get('api/lead-status/active-all')
      this.statusIdOption = (status?.data?.data || []).map(item => {
        let position = ''

        if (item?.type == 2) {
          position = '(Success)'
        } else if (item?.type == 3) {
          position = '(Dead)'
        }
        const name = `${item?.name} ${position}`
        return {
          name,
          id: item.id,
        }
      })
    },
    async loadAvailableStatus() {
      try {
        const [status] = await Promise.all([this.getLeadStatus()])

        const existingStatusId = this.lead?.leadStatus?.data?.sequence

        let availableStatus = []
        availableStatus = (status?.data?.data || []).filter(item => {
          if (item?.sequence >= existingStatusId) {
            const { name } = item
            return {
              name,
              id: item.id,
            }
          }
        })
        this.statusIdOption = availableStatus
        this.statusIdOption = (availableStatus || []).map(item => {
          let position = ''

          if (item?.type == 2) {
            position = '(Success)'
          } else if (item?.type == 3) {
            position = '(Dead)'
          }
          const name = `${item?.name} ${position}`
          return {
            name,
            type: item?.type,
            id: item.id,
          }
        })
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Warning',
            icon: 'BellIcon',
            variant: 'warning',
            text: error?.response?.data?.message,
          },
        })
      }
    },
    formatFnTableLastContactDate(value) {
      if (value) {
        return this.$moment(value).format('MMM Do YYYY')
      }
    },
    formatFnTableNextContactDate(value) {
      if (value) {
        return this.$moment(value).format('MMM Do YYYY')
      }
      return 'N/A'
    },

    onSearchUsers(search, loading) {
      if (search.length) {
        const { id } = this.$route.params
        loading(true)
        if (this.delayTimer) {
          clearTimeout(this.delayTimer)
          this.delayTimer = null
        }

        this.delayTimer = setTimeout(async () => {
          const resUsers = await this.$api.get(
            `api/department/${id}/users/search?include=department`,
            {
              params: {
                q: search,
              },
            },
          )

          const dataUsers = resUsers?.data?.data
          this.usersOption = (dataUsers || []).map(item => {
            let { name } = item
            let department = null

            if (item.department_id) {
              name = `${item.name} (${item?.department?.data?.name})`
              department = item?.department?.data?.name
            }

            return {
              name,
              id: item.id,
              department,
            }
          })
          loading(false)
        }, 800)
      }
    },
    async loadAllStatus() {
      //status
      const status = await this.$api.get("api/lead-status/active-all");
      this.statusIdOption = (status?.data?.data || []).map((item) => {
        let position = "";

        if (item?.type == 2) {
          position = "(Success)";
        } else if (item?.type == 3) {
          position = "(Dead)";
        }
        let name = `${item?.name} ${position}`;
        return {
          name,
          id: item.id,
        };
      });
    },
    async onShowEditLead(value) {
      this.modelType = "editModel";

      let dialCodeValue = value?.dialCodeCountry?.data?.dial_code;
      let mobile = value?.mobile
          ? value?.mobile.split(dialCodeValue)
          : value?.mobile;

      this.leadId = value?.id;
      this.lead_category_id = +value?.lead_category_id;
      this.lead_source_id = +value?.lead_source_id;
      this.department_id = +value?.department_id;
      this.kam = value?.kam;
      // this.lead_status_id = +value?.lead_status_id;
      // this.user_id = +value?.user_id;
      this.assign_users = (value?.team.data || []).map(
          (item) => item?.id
      );
      this.title = value?.title;
      this.keyContactPerson = value?.key_contact_person;
      this.company_name = value?.company_name;
      this.country_id = value?.country_id;
      this.email = value?.email;
      this.dial_code_country_id = value?.dial_code_country_id;
      this.mobile = value?.mobile;
      this.phone = value?.phone;
      this.address = value?.address;
      this.billing_address = value?.billing_address;
      this.city = value?.city;
      this.state = value?.state;
      this.street = value?.street;
      this.zip_code = value?.zip_code;
      this.note = value?.note;
      this.website_link = value?.website_link;
      this.last_contact_date = value?.last_contact_date;
      this.business_card_front = value?.business_card_front;
      this.business_card_back = value?.business_card_back;

      // this.loadAvailableStatus();

      this.showLeadEditModal();
    },
    showLeadEditModal() {
      this.$bvModal.show("modal-lead-form");
      this.loadAllStatus();
    },
    hiddenLeadEditModal() {
      this.modelType = "";
      this.$bvModal.hide("modal-lead-form");
      this.resetLeadEditModal();
    },
    resetLeadEditModal() {
      this.leadId = "";
      this.lead_category_id = "";
      this.lead_source_id = "";
      this.department_id = "";
      this.lead_status_id = "";
      this.assign_users = [];
      this.title = "";
      this.keyContactPerson = "";
      this.company_name = "";
      this.dial_code = "";
      this.country_id = "";
      this.email = "";
      this.mobile = "";
      this.phone = "";
      this.address = "";
      this.city = "";
      this.state = "";
      this.street = "";
      this.zip_code = "";
      this.note = "";
      this.website_link = "";
      this.last_contact_date = "";
      this.business_card_front = "";
      this.business_card_back = "";
      this.business_card_front_input = null;
      this.business_card_back_input = null;
    },
    setDialCodeAsCountry(countryId) {
      this.dial_code_country_id = countryId;
    },
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
    },
    async getActiveUsers() {
      return await this.$api.get("api/users/active-all");
    },
    async getLeadCategories() {
      return await this.$api.get("api/lead-categories/active-all");
    },
    async getLeadSources() {
      return await this.$api.get("api/lead-sources/active-all");
    },
    async getDepartments() {
      return await this.$api.get("api/departments/all");
    },
    async getCountries() {
      return await this.$api.get("api/country/all");
    },
    async getLeadStatusInfo() {
      return await this.$api.get("api/lead-status/active-all");
    },
    formatLeadStatus(leadStatus) {
      this.leadStatusInfo = [
        {
          id: "",
          name: "All",
          leadColorCode: "#000000" // Optional color code for "All" option
        },
        ...(leadStatus?.data?.data || []).map((item) => {
          return {
            id: item.id,
            name: item.name,
            leadCount: item.leads_count,
            leadColorCode: item.color_code,
          };
        }),
      ];
    },
    async validationLeadForm() {
      try {
        const isValid = await this.$refs.leadValidation.validate();
        if (!isValid) return;

        this.isLeadsFormSubmitLoading = true;
        this.$refs.leadValidation.reset();

        // Prepare form data
        const formData = new FormData();
        const fields = {
          lead_category_id: this.lead_category_id,
          lead_source_id: this.lead_source_id,
          department_id: this.department_id,
          title: this.title,
          key_contact_person: this.keyContactPerson,
          kam: this.kam,
          company_name: this.company_name,
          dial_code_country_id: this.dial_code_country_id,
          country_id: this.country_id,
          email: this.email,
          mobile: this.mobile,
          phone: this.phone,
          address: this.address,
          city: this.city,
          state: this.state,
          street: this.street,
          zip_code: this.zip_code,
          note: this.note,
          website_link: this.website_link,
          billing_address: this.billing_address
        };

        // Append fields
        Object.entries(fields).forEach(([key, value]) => {
          if (value !== undefined && value !== null) formData.append(key, value);
        });

        // Append array fields
        this.assign_users.forEach((user, index) => {
          formData.append(`assign_users[${index}]`, user);
        });

        // Append files
        if (this.business_card_back_input) {
          formData.append("business_card_back", this.business_card_back_input);
        }
        if (this.business_card_front_input) {
          formData.append("business_card_front", this.business_card_front_input);
        }

        // Configure request based on model type
        let endpoint = '/api/leads';
        let successMessage = 'Lead Created Successfully';

        if (this.modelType === "editModel") {
          formData.append("_method", "PUT");
          endpoint = `/api/leads/${this.leadId}`;
          successMessage = 'Lead Updated Successfully';
        }
        else if (this.modelType === "copyModel") {
          endpoint = '/api/leads/copy';
          formData.append("lead_status_id", this.lead_status_id);
          successMessage = 'Lead Copied Successfully';
        }
        else {
          formData.append("lead_status_id", this.lead_status_id);
        }

        // Make API call
        await this.$api.post(endpoint, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });

        // Handle success
        try {
          const [leadStatus] = await Promise.all([
            this.getLeadStatusInfo(),
            this.loadItems(),
          ]);
          this.formatLeadStatus(leadStatus);
        } catch (error) {
          console.log('error 1', error);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Error",
              icon: "BellIcon",
              variant: "danger",
              text: error?.response?.data?.message,
            },
          });
        }

        this.loadItems();
        this.hiddenLeadEditModal();
        this.loadLeadData();
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Success",
            icon: "BellIcon",
            variant: "success",
            text: successMessage,
          },
        });

      } catch (error) {
        console.log('error 2', error);
        this.isLeadsFormSubmitLoading = false;

        if (error?.response?.data?.message) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Error",
              icon: "BellIcon",
              variant: "danger",
              text: error?.response?.data?.message,
            },
          });
        }

        if (error?.response?.data?.errors) {
          this.$refs.leadValidation.setErrors(error?.response?.data?.errors);
        }
      } finally {
        this.isLeadsFormSubmitLoading = false;
      }
    },
    async onShow(value) {
      this.modelType = 'editModel'
      this.leadActivityLogId = value?.id
      this.contactMedium = value?.contact_through
      this.remarks = value?.remarks
      this.contactDate = value?.last_contact_date
      this.nextContactDate = value?.next_contact_date
      this.leadStatusId = this.lead?.lead_status_id
      this.showModal()
    },
    showModal() {
      this.leadStatusId = this.lead?.leadStatus?.data?.id
      this.$bvModal.show('modal-activity-log-show-form')
      this.loadAvailableStatus()
    },

    newLogModal() {
      this.modelType = ''
      this.showModal()
    },

    showEstimationModal() {
      this.selectedLeadInfo = {
        leadId: this.lead?.id,
        departmentId: this.lead?.department_id,
      }
      this.$bvModal.show('modal-financial-estimation-show-form-on-lead')
    },


    viewOnlyEstimationModal() {
      this.selectedLeadInfo = {
        leadId: this.lead?.id,
        departmentId: this.lead?.department_id,
      }

      this.viewOnly = true

      this.$bvModal.show('modal-financial-estimation-show-form-on-lead')
    },

    hideEstimationModal() {
      this.viewOnly = false;
    },
    resetEstimationModal() {
      this.projectType = ''
      this.amount = ''
      this.timeDuration = ''
      this.milestones = ''
      this.paymentType = ''
      this.milestoneAmmount = []
      this.milestoneDeadLine = []
      this.milestonePaymentType = []
    },
    hiddenModal() {
      this.$bvModal.hide('modal-activity-log-show-form')
      this.resetModal()
    },
    resetModal() {
      this.leadId = ''
      this.leadActivityLogId = ''
      this.contactMedium = ''
      this.file = []
      this.remarks = ''
      this.nextContactDate = ''
      this.leadStatusId = ''
      this.selectedLeadStatus = ''
    },

    async getLeadAcitvityLog(params) {
      this.leadId = this.$route.params.id
      return await this.$api.get(
        `api/leads/${this.leadId}/lead-activity-log?include=leadStatus`,
        {
          params: {
            show: params.show,
            page: params.page,
            sort: params.sort,
            q: params.q,
          },
        },
      )
    },
    updateLeadInfo() {
      this.loadLeadData()
      this.showFinancialEstimationComponent = false
    },
    async getLeadStatus() {
      return await this.$api.get('api/lead-status/active-all')
    },
    async onDelete(id) {
      this.$swal({
        title: 'Warning!',
        text: 'Are You Sure You Want To Delete This?',
        icon: 'warning',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: 'Ok',
        showLoaderOnConfirm: true,
      }).then(async result => {
        if (result.isConfirmed) {
          try {
            await this.$api.delete(`/api/lead-activity-log/${id}`)

            this.loadItems()

            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Success',
                icon: 'BellIcon',
                variant: 'success',
                text: 'Successfully Deleted',
              },
            })
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Error',
                icon: 'BellIcon',
                variant: 'danger',
                text: error?.response?.data?.message,
              },
            })
          }
        }
      })
    },

    onSearch() {
      if (this.delayTimer) {
        clearTimeout(this.delayTimer)
        this.delayTimer = null
      }

      this.delayTimer = setTimeout(() => {
        this.loadItems()
      }, 1000)
    },

    updateParams(newProps) {
      this.serverParams = { ...this.serverParams, ...newProps }
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage })
      this.loadItems()
    },

    onPerPageChange(params) {
      this.updateParams({ perPage: params.currentPerPage })
      this.loadItems()
    },

    onSortChange(params) {
      this.updateParams({
        sort: params,
      })
      this.loadItems()
    },

    onColumnFilter(params) {
      this.updateParams(params)
      this.loadItems()
    },

    async loadItems() {
      try {
        const [leadAcitvityLog] = await Promise.all([
          this.getLeadAcitvityLog({
            show: this.serverParams.perPage,
            page: this.serverParams.page,
            sort: this.serverParams.sort,
            q: this.searchTerm,
          }),
        ])

        const data = leadAcitvityLog?.data?.data

        const meta = leadAcitvityLog?.data?.meta

        this.totalRecords = meta?.pagination?.total
        this.rows = data
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Warning',
            icon: 'BellIcon',
            variant: 'warning',
            text: error?.response?.data?.message,
          },
        })
      }
    },

    async validationForm() {
      const validateForm = async () => this.$refs.leadActvityLogValidation.validate()

      const resetForm = () => {
        this.$refs.leadActvityLogValidation.reset()
      }

      const showToast = (title, text, variant = 'success') => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title, icon: 'BellIcon', variant, text,
          },
        })
      }

      const fetchLeadData = async () => {
        try {
          const { id } = this.$route.params
          const resLead = await this.$api.get(
            `api/leads/${id}?include=user,leadCategory,leadSource,leadStatus,department,leadCountry,dialCodeCountry`,
          )
          this.lead = resLead?.data?.data
        } catch (error) {
          showToast('Error', error?.response?.data?.message, 'danger')
        }
      }

      const prepareFormData = () => {
        const formData = new FormData()

        if (this.modelType === 'editModel') {
          formData.append('_method', 'PUT')
        }

        if (this.leadId) formData.append('lead_id', this.leadId)
        if (this.contactMedium) formData.append('contact_through', this.contactMedium)
        if (this.remarks) formData.append('remarks', this.remarks)
        if (this.leadStatusId) formData.append('lead_status_id', this.leadStatusId)

        formData.append(
          'lead_status_type',
            this.selectedLeadStatus?.type || this.leadStatusTypeConstants[0]?.value,
        )

        this.file.forEach((file, index) => {
          formData.append(`lead_activity_log_file[${index}]`, file)
        })

        formData.append('next_contact_date', this.nextContactDate)
        formData.append('contact_date', this.contactDate)

        return formData
      }

      const submitForm = async formData => {
        const url = this.modelType === 'editModel'
          ? `/api/lead-activity-log/${this.leadActivityLogId}`
          : '/api/lead-activity-log'

        await this.$api.post(url, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })

        showToast('Success', 'Lead Activity Log Submitted Successfully')
      }

      try {
        if (await validateForm()) {
          resetForm()
          this.isLeadActivityFormSubmitLoading = true
          this.leadId = this.$route.params.id

          const formData = prepareFormData()
          await submitForm(formData)

          this.isLeadActivityFormSubmitLoading = false
          this.hiddenModal()
          this.loadItems()
          await fetchLeadData()
          this.loadAvailableStatus()
        }
      } catch (error) {
        this.isLeadActivityFormSubmitLoading = false

        if (error?.response?.data?.message) {
          showToast('Error', error?.response?.data?.message, 'danger')
        }

        if (error?.response?.data?.errors) {
          this.$refs.leadActvityLogValidation.setErrors(error?.response?.data?.errors)
        }
      }
    },

  },
}
</script>

<style lang="scss">
.custom-font {
  font-size: 11px !important;
}
.lead-details-table {
  // font-size: 13px !important;
  // font-weight: 600 !important;
  min-height: 140px !important;
  white-space: nowrap !important;
  font-size: 13px !important;
  tr,
  th,
  td {
    vertical-align: left !important;
    text-align: left !important;
  }
}

.required-label label::after {
  content: " *";
  color: red;
}

@import "@core/scss/vue/libs/vue-good-table.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

.small-image {
  max-width: 150px; /* Limits the width to make the image smaller */
  height: auto; /* Maintains the aspect ratio */
}

.flatpickr-wrapper{
  width: 100% !important;
}

@import "@core/scss/vue/libs/quill.scss";


.table-custom-style {
  font-size: 13px !important;
  white-space: nowrap !important;
  min-height: 140px !important;
  tr,
  th,
  td {
    vertical-align: left !important;
    text-align: left !important;
  }
}

.custom-font {
  font-size: 13px !important;
}
.custom-font-validation {
  font-size: 11px;
}
.cell-with-description div {
  margin: 2px 0px;
}
.required-label label::after {
  content: " *";
  color: red;
}
.table-custom-style {
  font-size: 14px !important;
  white-space: nowrap !important;
  min-height: 140px !important;
  tr,
  th,
  td {
    vertical-align: middle !important;
  }
}
.custom-font {
  font-size: 13px !important;
}
.small-image {
  max-width: 150px; /* Limits the width to make the image smaller */
  height: auto; /* Maintains the aspect ratio */
}
/* Adjust editor and toolbar in fullscreen mode */
.fullscreen .ql-container {
  height: calc(100vh - 50px) !important;
}

.fullscreen .ql-toolbar {
  position: sticky;
  top: 0;
  background: white;
  z-index: 1000;
  padding: 10px;
}


/* Fullscreen mode */
.fullscreen {
  position: fixed !important;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: white;
  z-index: 9999;
  padding: 10px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}
/* Fullscreen button styling */
.fullscreen-btn {
  border: none;
  background: none;
  cursor: pointer;
  font-size: 18px;
  margin-left: 10px;
}
</style>

